import React, { useEffect, useState } from "react";
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";

export const EnhancedErrorHandler = ({ error, duration = 6000 }) => {
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (error?.message) {
      setOpen(true);

      const timer = setTimeout(() => {
        setOpen(false);
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [error, duration]);

  // If error is a simple string or basic error object, convert to enhanced format
  const normalizedError = React.useMemo(() => {
    if (!error) return null;

    if (typeof error === "string") {
      return {
        message: error,
        timestamp: new Date().toISOString(),
        code: "unknown",
      };
    }

    // If it's already our enhanced error format
    if (error.timestamp && error.code) {
      return error;
    }

    // If it's a basic error object
    return {
      message: error.message || "An unknown error occurred",
      code: error.code || "unknown",
      timestamp: new Date().toISOString(),
      status: error.status,
      details: error.details || null,
      requestParams: error.requestParams || null,
    };
  }, [error]);

  const handleCopyError = () => {
    const errorText = JSON.stringify(normalizedError, null, 2);
    navigator.clipboard.writeText(errorText);
  };

  const hasDetails =
    normalizedError &&
    (normalizedError.code !== "unknown" ||
      normalizedError.details ||
      normalizedError.requestParams ||
      normalizedError.status);

  const formatErrorDetails = () => {
    return (
      <Box sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Error Code: {normalizedError.code}
        </Typography>
        {normalizedError.status && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Status: {normalizedError.status}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Time: {normalizedError.timestamp}
        </Typography>
        {normalizedError.details && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Details: {JSON.stringify(normalizedError.details, null, 2)}
          </Typography>
        )}
        {normalizedError.requestParams && (
          <Typography variant="body2" color="text.secondary">
            Request Parameters:{" "}
            {JSON.stringify(normalizedError.requestParams, null, 2)}
          </Typography>
        )}
      </Box>
    );
  };

  if (!normalizedError) return null;

  return (
    <>
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setOpen(false)}
          action={
            hasDetails ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => setDetailsOpen(true)}
              >
                View Details
              </Button>
            ) : undefined
          }
        >
          Error: {normalizedError.message}
        </Alert>
      </Snackbar>

      {hasDetails && (
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Error Details
            <IconButton
              aria-label="close"
              onClick={() => setDetailsOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>{formatErrorDetails()}</DialogContent>
          <DialogActions>
            <Button startIcon={<ContentCopyIcon />} onClick={handleCopyError}>
              Copy Error Details
            </Button>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
